import { Controller } from '@hotwired/stimulus'
import { addToArray, removeFromArray } from '~/utils/array'
import { Turbo } from '@hotwired/turbo-rails'

export default class extends Controller {
  static values = {
    selectedAges: { type: Array, default: [] },
    selectedVisitorIds: { type: Array, default: [] },
    selectedActivities: { type: Array, default: [] },
    selectedDates: { type: Array, default: [] },
    selectedOptions: { type: Array, default: [] },
    selectedPrice: { type: Array },
    activitiesRangePrice: { type: Array },
    selectedDistance: { type: String }, // String because it can be 'unlimited_distance'
    visitorData: { type: Object, default: {} }
  }

  static targets = [
    'selectedDates',
    'selectedAges',
    'selectedVisitorIds',
    'selectedActivities',
    'selectedOptions',
    'selectedPrice',
    'selectedDistance',
    'hideSoldOutDesktopInput',
    'hideSoldOutMobileInput',
    'quickBookDesktopInput',
    'quickBookMobileInput'
  ]

  connect() {
    this.selectedPriceChanged()
    this.initialLoad = true
    this.form = document.getElementById('filter-form')
  }

  selectedAgesValueChanged() {
    this.selectedAgesTargets.map((element) => {
      if (this.selectedAgesValue.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
    this.updateTextTargets()
  }

  selectedVisitorIdsValueChanged() {
    this.selectedVisitorIdsTargets.map((element) => {
      if (this.selectedVisitorIdsValue.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
    this.updateTextTargets()
  }

  selectedPriceChanged() {
    if (this.#arraysEqual(this.selectedPriceValue, this.activitiesRangePriceValue)) {
      this.selectedPriceTarget.parentElement.classList.remove('text-blue-400')
    } else {
      this.selectedPriceTarget.parentElement.classList.add('text-blue-400')
    }
    this.updateTextTargets()
  }

  #arraysEqual(a, b) {
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false
    }

    return true
  }

  selectedActivitiesValueChanged() {
    this.selectedActivitiesTargets.map((element) => {
      if (this.selectedActivitiesValue.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
    this.updateTextTargets()
  }

  selectedOptionsValueChanged(value) {
    this.selectedOptionsTargets.map((element) => {
      if (value.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
    this.updateTextTargets()
  }

  selectedDatesValueChanged() {
    this.selectedDatesTargets.map((element) => {
      if (this.selectedDatesValue.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
    this.updateTextTargets()
  }

  selectedDistanceValueChanged(value) {
    const element = this.selectedDistanceTarget
    if (value === 'unlimited_distance' || Number(value)) {
      element.parentElement.classList.add('text-blue-400')
    } else {
      element.parentElement.classList.remove('text-blue-400')
    }
  }

  updatePriceValue(event) {
    if (event.detail && event.detail.minPrice !== undefined && event.detail.maxPrice !== undefined) {
      const minPrice = event.detail.minPrice;
      const maxPrice = event.detail.maxPrice;

      if (!isNaN(minPrice) && !isNaN(maxPrice)) {
        this.selectedPriceValue = [minPrice, maxPrice];
        this.selectedPriceChanged();
      }
    }
  }

  clearDates() {
    this.selectedDatesValue = []
  }

  /**
   * Maneja la selección/deselección manual de filtros de edad.
   * Si se desmarca una edad, verifica automáticamente qué visitors ya no tienen
   * todas sus opciones seleccionadas y los desmarca, pero preserva otros filtros manuales.
   */
  setAges(event) {
    const checkbox = event.target
    const value = checkbox.value

    if (checkbox.checked) {
      this.selectedAgesValue = addToArray(this.selectedAgesValue, value)
    } else {
      this.selectedAgesValue = removeFromArray(this.selectedAgesValue, value)
      // Check if any visitor no longer matches completely
      this.checkAndUpdateVisitorsAfterManualChange()
    }

    this.updateUrl()
  }

  /**
   * Maneja la selección/deselección de visitors.
   * Cuando se selecciona un visitor, automáticamente aplica sus filtros (ages + interests).
   * Cuando se deselecciona, elimina solo los filtros específicos de ese visitor,
   * preservando filtros manuales agregados por el usuario.
   */
  setVisitors(event) {
    const checkbox = event.target
    const value = checkbox.value

    if (checkbox.checked) {
      this.selectedVisitorIdsValue = addToArray(this.selectedVisitorIdsValue, value)
    } else {
      this.selectedVisitorIdsValue = removeFromArray(this.selectedVisitorIdsValue, value)
    }

    // Apply automatic filters based on selected visitors
    this.applyVisitorBasedFilters()

    this.updateUrl()
  }

  /**
   * Maneja la selección/deselección manual de filtros de actividades/intereses.
   * Si se desmarca un interés, verifica automáticamente qué visitors ya no tienen
   * todas sus opciones seleccionadas y los desmarca, pero preserva otros filtros manuales.
   */
  setActivities(event) {
    const checkbox = event.target
    const value = checkbox.value

    if (checkbox.checked) {
      this.selectedActivitiesValue = addToArray(this.selectedActivitiesValue, value)
    } else {
      this.selectedActivitiesValue = removeFromArray(this.selectedActivitiesValue, value)
      // Check if any visitor no longer matches completely
      this.checkAndUpdateVisitorsAfterManualChange()
    }

    this.updateUrl()
  }

  setDates(event) {
    const checkbox = event.target
    const value = checkbox.value

    if (checkbox.checked) {
      this.selectedDatesValue = addToArray(this.selectedDatesValue, value)
    } else {
      this.selectedDatesValue = removeFromArray(this.selectedDatesValue, value)
    }

    this.updateUrl()
  }

  setOptions(event) {
    const checkbox = event.target
    const value = checkbox.value

    if (checkbox.checked) {
      this.selectedOptionsValue = addToArray(this.selectedOptionsValue, value)
    } else {
      this.selectedOptionsValue = removeFromArray(this.selectedOptionsValue, value)
    }

    this.updateUrl()
  }

  setPrice(event) {
    const priceInputs = document.querySelectorAll('input[name="price"]')
    const priceValues = []

    priceInputs.forEach(input => {
      priceValues.push(input.value)
    })

    this.selectedPriceValue = priceValues
    this.updateUrl()
  }

  setDistance(event) {
    const input = event.target
    this.selectedDistanceValue = input.value
    this.updateUrl()
  }

  clear() {
    this.selectedAgesValue = []
    this.selectedVisitorIdsValue = []
    this.selectedActivitiesValue = []
    this.selectedDatesValue = []
    this.selectedOptionsValue = []
    this.selectedPriceValue = []
    this.selectedDistanceValue = ''

    this.updateUrl()
  }

  /**
   * Apply filters based on selected visitors using pre-established data.
   * Intelligently combines automatic filters (from visitors) with manual filters (user added).
   * Preserves manual filters while updating automatic ones based on current visitor selection.
   */
  applyVisitorBasedFilters() {
    // Get all automatic filters that should be based on selected visitors
    const expectedAutoAges = this.getExpectedAutoFilters('age_groups')
    const expectedAutoInterests = this.getExpectedAutoFilters('interests')

    // Identify current filters that are automatic vs manual
    const currentAutoAges = this.getCurrentAutoFilters('ages')
    const currentAutoInterests = this.getCurrentAutoFilters('interests')

    // Manual filters = filters that are NOT in any visitor
    const manualAges = this.selectedAgesValue.filter(age => !currentAutoAges.includes(age))
    const manualInterests = this.selectedActivitiesValue.filter(interest => !currentAutoInterests.includes(interest))

    // Combine expected automatic filters + manual filters
    this.selectedAgesValue = [...new Set([...expectedAutoAges, ...manualAges])]
    this.selectedActivitiesValue = [...new Set([...expectedAutoInterests, ...manualInterests])]

    // Update the interface
    this.updateVisibleFilters()
  }

  /**
   * Get expected automatic filters based on selected visitors.
   * Returns array of filters that should be active based on currently selected visitors.
   */
  getExpectedAutoFilters(filterType) {
    if (this.selectedVisitorIdsValue.length === 0) {
      return []
    }

    const selectedVisitorData = this.selectedVisitorIdsValue.map(visitorId => {
      return this.visitorDataValue[visitorId]
    }).filter(Boolean)

    if (selectedVisitorData.length === 0) {
      return []
    }

    return selectedVisitorData
      .flatMap(visitor => visitor[filterType] || [])
      .filter((value, index, self) => self.indexOf(value) === index) // unique
  }

  /**
   * Get current automatic filters (that exist in any visitor).
   * Used to identify which current filters are automatic vs manual.
   */
  getCurrentAutoFilters(filterType) {
    if (!this.visitorDataValue) return []

    const allVisitorFilters = Object.values(this.visitorDataValue)
      .flatMap(visitor => visitor[filterType === 'ages' ? 'age_groups' : 'interests'] || [])
      .filter((value, index, self) => self.indexOf(value) === index) // unique

    return allVisitorFilters
  }

  /**
   * Check and update visitors after manual changes to filters.
   * When a user manually unchecks a filter, this method verifies which visitors
   * no longer have ALL their options selected and automatically unchecks them.
   * Preserves manual filter selections and only affects visitor checkboxes.
   */
  checkAndUpdateVisitorsAfterManualChange() {
    if (!this.visitorDataValue || this.selectedVisitorIdsValue.length === 0) {
      return
    }

    const visitorsToKeep = []

    // Check each selected visitor
    this.selectedVisitorIdsValue.forEach(visitorId => {
      const visitorData = this.visitorDataValue[visitorId]
      if (!visitorData) return

      // Check if ALL age_groups of the visitor are selected
      const visitorAgeGroups = visitorData.age_groups || []
      const allAgeGroupsSelected = visitorAgeGroups.every(ageGroup =>
        this.selectedAgesValue.includes(ageGroup)
      )

      // Check if ALL interests of the visitor are selected
      const visitorInterests = visitorData.interests || []
      const allInterestsSelected = visitorInterests.every(interest =>
        this.selectedActivitiesValue.includes(interest)
      )

      // Only keep the visitor if ALL their options are still selected
      if (allAgeGroupsSelected && allInterestsSelected) {
        visitorsToKeep.push(visitorId)
      }
    })

    // Update selected visitors only with those that still match completely
    if (visitorsToKeep.length !== this.selectedVisitorIdsValue.length) {
      this.selectedVisitorIdsValue = visitorsToKeep

      // DO NOT recalculate filters automatically - maintain user's manual selections
      // Only update visitor checkboxes in the interface
      this.updateVisitorCheckboxes()

      // Update visitor text
      this.updateTextTargets()
    }
  }

  /**
   * Update visitor checkboxes in the interface.
   * Synchronizes the visual state of visitor checkboxes with the internal state.
   */
  updateVisitorCheckboxes() {
    const visitorCheckboxes = document.querySelectorAll('input[name="visitor_ids[]"]')
    visitorCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedVisitorIdsValue.includes(checkbox.value)
    })
  }

  /**
   * Update the visual display of filters.
   * Synchronizes all filter checkboxes and text targets with internal state.
   */
  updateVisibleFilters() {
    // Update age checkboxes
    const ageCheckboxes = document.querySelectorAll('input[name="ages[]"]')
    ageCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedAgesValue.includes(checkbox.value)
    })

    // Update activity checkboxes
    const activityCheckboxes = document.querySelectorAll('input[name="interests_text[]"]')
    activityCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedActivitiesValue.includes(checkbox.value)
    })

    // Update text targets
    this.updateTextTargets()
  }

  updateTextTargets() {
    if (this.hasSelectedAgesTarget) {
      this.selectedAgesTarget.textContent = this.selectedAgesValue.length > 0 ?
        `Age (${this.selectedAgesValue.length})` : 'Age'
    }

    if (this.hasSelectedActivitiesTarget) {
      this.selectedActivitiesTarget.textContent = this.selectedActivitiesValue.length > 0 ?
        `Activities (${this.selectedActivitiesValue.length})` : 'Activities'
    }

    if (this.hasSelectedOptionsTarget) {
      this.selectedOptionsTarget.textContent = this.selectedOptionsValue.length > 0 ?
        `Options (${this.selectedOptionsValue.length})` : 'Options'
    }

    if (this.hasSelectedVisitorIdsTarget) {
      this.selectedVisitorIdsTarget.textContent = this.selectedVisitorIdsValue.length > 0 ?
        `Family (${this.selectedVisitorIdsValue.length})` : 'My Family'
    }
  }

  updateUrl() {
    const params = new URLSearchParams()

    // Add parameters only if they have values
    if (this.selectedAgesValue.length > 0) {
      this.selectedAgesValue.forEach(age => params.append('ages[]', age))
    }

    if (this.selectedVisitorIdsValue.length > 0) {
      this.selectedVisitorIdsValue.forEach(id => params.append('visitor_ids[]', id))
    }

    if (this.selectedActivitiesValue.length > 0) {
      this.selectedActivitiesValue.forEach(activity => params.append('interests_text[]', activity))
    }

    if (this.selectedDatesValue.length > 0) {
      this.selectedDatesValue.forEach(date => params.append('dates[]', date))
    }

    if (this.selectedOptionsValue.length > 0) {
      this.selectedOptionsValue.forEach(option => params.append('activity_options[]', option))
    }

    // Only add price if it's different from default
    if (this.selectedPriceValue && this.selectedPriceValue.length > 0) {
      const activitiesMinPriceEl = document.querySelector('input[name="activities_min_price"]')
      const activitiesMaxPriceEl = document.querySelector('input[name="activities_max_price"]')

      if (activitiesMinPriceEl && activitiesMaxPriceEl) {
        const defaultMinPrice = parseInt(activitiesMinPriceEl.value)
        const defaultMaxPrice = parseInt(activitiesMaxPriceEl.value)
        const currentMinPrice = parseInt(this.selectedPriceValue[0])
        const currentMaxPrice = parseInt(this.selectedPriceValue[1])

        // Only add price parameters if they're different from defaults
        if (currentMinPrice !== defaultMinPrice || currentMaxPrice !== defaultMaxPrice) {
          params.append('min_price', currentMinPrice)
          params.append('max_price', currentMaxPrice)
        }
      }
    }

    if (this.selectedDistanceValue && this.selectedDistanceValue !== '') {
      params.append('distance', this.selectedDistanceValue)
    }

    // Preserve other parameters that may exist
    const currentParams = new URLSearchParams(window.location.search)
    const preserveParams = ['address', 'latitude', 'longitude']

    preserveParams.forEach(param => {
      if (currentParams.has(param)) {
        params.set(param, currentParams.get(param))
      }
    })

    // Update URL without reloading the page and WITHOUT automatic search
    const newUrl = `${window.location.pathname}?${params.toString()}`

    if (newUrl !== window.location.href) {
      window.history.pushState({}, '', newUrl)
      // DO NOT call performSearch() here - only update the URL
    }

    this.initialLoad = false
  }

  performSearch() {
    // Use Turbo to perform search without completely reloading the page
    Turbo.visit(window.location.href, { frame: 'activities-list' })
  }

  submit(event) {
    event.preventDefault();

    const currentInput = event.target

    if (currentInput.type === 'button' && currentInput.innerText === 'Apply') {
      const priceSliderElement = document.querySelector('[data-controller="price-slider"]');
      if (priceSliderElement) {
        const minPriceInput = priceSliderElement.querySelector('[data-price-slider-target="minPrice"]');
        const maxPriceInput = priceSliderElement.querySelector('[data-price-slider-target="maxPrice"]');

        if (minPriceInput && maxPriceInput) {
          const minPrice = parseInt(minPriceInput.value);
          const maxPrice = parseInt(maxPriceInput.value);
          this.selectedPriceValue = [minPrice, maxPrice];
        }
      }
    }

    if (currentInput.name === 'hide_sold_out') {
      if (this.hasHideSoldOutDesktopInputTarget && this.hideSoldOutDesktopInputTarget !== currentInput) {
        this.hideSoldOutDesktopInputTarget.checked = currentInput.checked
      }
      if (this.hasHideSoldOutMobileInputTarget && this.hideSoldOutMobileInputTarget !== currentInput) {
        this.hideSoldOutMobileInputTarget.checked = currentInput.checked
      }
    }

    if (currentInput.name === 'quick_book') {
      if (this.hasQuickBookDesktopInputTarget && this.quickBookDesktopInputTarget !== currentInput) {
        this.quickBookDesktopInputTarget.checked = currentInput.checked
      }
      if (this.hasQuickBookMobileInputTarget && this.quickBookMobileInputTarget !== currentInput) {
        this.quickBookMobileInputTarget.checked = currentInput.checked
      }
    }

    const form = currentInput.closest('form')
    if (form) {
      this.#constructAndSubmitParams(form)
    } else {
      const controllerForm = this.element.closest('form')
      if (controllerForm) {
        this.#constructAndSubmitParams(controllerForm)
      }
    }
  }

  #constructAndSubmitParams(form) {
    // Sync internal state with form checkboxes before submission
    this.syncFormCheckboxes()

    const params = new URLSearchParams()
    const formData = new FormData(form)

    for (const [key, value] of formData.entries()) {
      // Skip empty values and system fields
      if (this.#shouldSkipParam(key, value, form)) {
        continue
      }

      // if the parameter is multiple, use append
      if ([
        'ages[]',
        'visitor_ids[]',
        'interests_text[]',
        'dates[]',
        'activity_options[]'
      ].includes(key)) {
        params.append(key, value)
      } else {
        params.set(key, value)
      }
    }

    // Only send hide_sold_out if it's different from default (1)
    const hideSoldOutDesktop = this.hasHideSoldOutDesktopInputTarget && this.hideSoldOutDesktopInputTarget?.checked;
    const hideSoldOutMobile = this.hasHideSoldOutMobileInputTarget && this.hasHideSoldOutMobileInputTarget?.checked;
    const hideSoldOutValue = hideSoldOutDesktop || hideSoldOutMobile ? '1' : '0';

    // Only add hide_sold_out if it's '0' (different from default '1')
    if (hideSoldOutValue === '0') {
      params.set('hide_sold_out', hideSoldOutValue);
    }

    const finalUrl = `${form.action}?${params.toString()}`
    Turbo.visit(finalUrl)
  }

  /**
   * Sync internal controller state with form checkboxes before submission.
   * This ensures that all selected values are properly reflected in the form.
   */
  syncFormCheckboxes() {
    // Sync ages checkboxes
    const ageCheckboxes = document.querySelectorAll('input[name="ages[]"]')
    ageCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedAgesValue.includes(checkbox.value)
    })

    // Sync visitor checkboxes
    const visitorCheckboxes = document.querySelectorAll('input[name="visitor_ids[]"]')
    visitorCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedVisitorIdsValue.includes(checkbox.value)
    })

    // Sync activities checkboxes
    const activityCheckboxes = document.querySelectorAll('input[name="interests_text[]"]')
    activityCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedActivitiesValue.includes(checkbox.value)
    })

    // Sync dates checkboxes
    const dateCheckboxes = document.querySelectorAll('input[name="dates[]"]')
    dateCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedDatesValue.includes(checkbox.value)
    })

    // Sync options checkboxes
    const optionCheckboxes = document.querySelectorAll('input[name="activity_options[]"]')
    optionCheckboxes.forEach(checkbox => {
      checkbox.checked = this.selectedOptionsValue.includes(checkbox.value)
    })
  }

  #shouldSkipParam(key, value, form) {
    // Skip authenticity token
    if (key === 'authenticity_token') {
      return true
    }

    // Skip empty strings
    if (value === '') {
      return true
    }

    // Skip hide_sold_out when it's '1' (default value)
    if (key === 'hide_sold_out' && value === '1') {
      return true
    }

    // Skip quick_book when it's '0' (default unchecked state)
    if (key === 'quick_book' && value === '0') {
      return true
    }

    // Skip recurring when it's '0' (default unchecked state)
    if (key === 'recurring' && value === '0') {
      return true
    }

    // Handle price filtering: skip if values match defaults
    if (key === 'min_price' || key === 'max_price') {
      const activitiesMinPriceEl = document.querySelector('input[name="activities_min_price"]')
      const activitiesMaxPriceEl = document.querySelector('input[name="activities_max_price"]')

      if (activitiesMinPriceEl && activitiesMaxPriceEl) {
        const defaultMinPrice = parseInt(activitiesMinPriceEl.value)
        const defaultMaxPrice = parseInt(activitiesMaxPriceEl.value)

        // Get current values from the form being submitted
        const formData = new FormData(form)
        const currentMinPrice = parseInt(formData.get('min_price') || '0')
        const currentMaxPrice = parseInt(formData.get('max_price') || '0')

        // If both min and max are at default values, skip both
        if (currentMinPrice === defaultMinPrice && currentMaxPrice === defaultMaxPrice) {
          return true
        }
      }
    }

    // Skip default/empty coordinates
    if (['ne_lat', 'ne_lng', 'sw_lat', 'sw_lng'].includes(key) && value === '') {
      return true
    }

    // Skip empty dates
    if (['started_at', 'finished_at'].includes(key) && value === '') {
      return true
    }

    // Skip unchecked checkboxes that shouldn't be sent
    if (key.includes('all_') && value === '0') {
      return true
    }

    // Skip default location fields if empty
    if (['address', 'longitude', 'latitude'].includes(key) && value === '') {
      return true
    }

    // Skip activities_min_price and activities_max_price (these are just reference values)
    if (['activities_min_price', 'activities_max_price'].includes(key)) {
      return true
    }

    return false
  }

  /**
   * Method to intercept form submission (when "Show results" is clicked).
   * Prevents default submission and uses custom parameter construction.
   */
  _handleFormSubmit(event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    this.#constructAndSubmitParams(event.target)
  }
}
